import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import { VideoCardProps } from "@fdr/atomic-ui/_molecules/VideoCard";
import VideoPlaylistTemplate from "@fdr/atomic-ui/_templates/VideoPlaylist";
import ModalV2 from "@tvg/atomic-ui/_templates/ModalV2";
import VideoPlayerDetails from "@fdr/atomic-ui/_templates/VideoPlayerDetails";
import mediator from "@tvg/mediator";
import { events as AlchemerEvents } from "@urp/alchemer";
import { getJWPlaylistVideos, toggleNativeRotation } from "./utils";
import { closeVideoModal, openVideoModal } from "./actions";
import {
  getDefaultPlaylistSettings,
  getFullscreenModalState
} from "./selectors";
import { JWPlayerProgramPageResponse, VideoNativeRotation } from "./types";

interface Props {
  playlistUrl?: string;
  disableFetchVideos?: boolean;
}

function VideoPlaylist(props: Props) {
  const dispatch = useDispatch();

  const isVideoModalOpen = useSelector(getFullscreenModalState);
  const { playlistUrl, playerScript, licenseKey } = useSelector(
    getDefaultPlaylistSettings
  );

  const videoCards = useSelector((store) =>
    get(store, "RaceProgram.videoCarousel.videoCards", {})
  );

  const [videos, setVideos] = useState<
    VideoCardProps[] | JWPlayerProgramPageResponse
  >([]);
  const [selectedVideo, setSelectedVideo] = useState<VideoCardProps>();
  useEffect(() => {
    if (!props.disableFetchVideos) {
      getJWPlaylistVideos(props.playlistUrl || playlistUrl, {
        playerScript,
        licenseKey
      }).then((playlistVideos) => setVideos(playlistVideos));
    }
  }, [props.playlistUrl, playlistUrl, playerScript, licenseKey]);

  const handleOpenVideoModal = (selected: VideoCardProps) => {
    AlchemerEvents.selectEducationVideo();
    setSelectedVideo(selected);
    dispatch(openVideoModal());
  };

  const handleCloseVideoModal = () => {
    dispatch(closeVideoModal());
  };

  const gtmHandlers = (
    type: string,
    payload: {
      errorType?: string;
      gaEventAction?: string;
    } = {},
    callback?: () => void
  ) => {
    if (selectedVideo?.description) {
      mediator.base.dispatch({
        type,
        payload: {
          videoProvider: "jw_player",
          videoTitle: selectedVideo?.description.title,
          ...payload
        }
      });

      if (callback) callback();
    }
  };

  return (
    <>
      <VideoPlaylistTemplate
        videos={props.disableFetchVideos ? videoCards : videos}
        isLoading={props.disableFetchVideos ? !videoCards.length : !videos}
        onCardClick={handleOpenVideoModal}
      />

      {selectedVideo && (
        <ModalV2
          offsetTop={0}
          animation="bottom"
          isOpen={isVideoModalOpen}
          onClose={handleCloseVideoModal}
        >
          {() => (
            <VideoPlayerDetails
              {...selectedVideo.description}
              src={selectedVideo.video.src}
              tracks={selectedVideo.video.tracks}
              library={selectedVideo.video.playerScript || playerScript}
              onClose={handleCloseVideoModal}
              isAutoPlay
              width={selectedVideo.video.width}
              height={selectedVideo.video.height}
              onPause={() => gtmHandlers("VP_VIDEO_PAUSED")}
              allowFullscreen
              onPlay={(viewable: boolean) =>
                gtmHandlers(
                  "VP_VIDEO_PLAY_CLICKED",
                  {},
                  () => viewable && gtmHandlers("VP_VIDEO_PLAYED_SUCCESSFULL")
                )
              }
              onVideoViewed={() => gtmHandlers("VP_VIDEO_VIEWED")}
              onVideoPlayedCompletly={() =>
                gtmHandlers("VP_VIDEO_PLAYED_COMPLETELY")
              }
              onVideoPlayAttemptFailed={(error: string) =>
                gtmHandlers("VP_VIDEO_PLAY_ERROR", {
                  errorType: error,
                  gaEventAction: error
                })
              }
              onSetupError={(error: string) =>
                gtmHandlers("VP_VIDEO_LOAD_FAILED", {
                  errorType: error,
                  gaEventAction: error
                })
              }
              onVideoMuteAndUnmute={(muted: boolean) =>
                gtmHandlers("VP_VIDEO_MUTED_UNMUTED", {
                  gaEventAction: muted
                    ? "Education Video Muted"
                    : "Education Video Unmuted"
                })
              }
              onVideoFullScreen={(fullscreen: boolean) =>
                gtmHandlers("VP_VIDEO_SIZE_CHANGED", {
                  gaEventAction: fullscreen
                    ? "Video View Maximised"
                    : "Video View Minimized"
                })
              }
              deviceLockRotationToggle={(isLock) => {
                toggleNativeRotation(
                  isLock
                    ? VideoNativeRotation.PORTRAIT
                    : VideoNativeRotation.ALL
                );
              }}
            />
          )}
        </ModalV2>
      )}
    </>
  );
}

export default VideoPlaylist;
